import "leaflet";
import "leaflet-gesture-handling";
import { removeMap } from "./map";

const gestureHandlingText = {
  touch: "Verschieben des Grundrisses mit zwei Fingern",
  scroll: "Verwende Strg+Scrollen zum Zoomen des Grundrisses",
  scrollMac: "Verwende \u2318+Scrollen zum Zoomen des Grundrisses"
};

// see Views/Content/ResponsiveImage.elm
const SIZE_BREAKPOINTS = [
  { key: "s", maxWidth: 767 },
  { key: "m", maxWidth: 991 },
  { key: "l", maxWidth: 1199 },
  { key: "xl", maxWidth: null }
];

// see Views/Content/ResponsiveImage.elm
const RATIO_THRESHOLDS = [
  { key: "4x", minRatio: 3.5 },
  { key: "3x", minRatio: 2.5 },
  { key: "2x", minRatio: 1.5 },
  { key: "1x", minRatio: null }
];

// Offscreen styling instead of `display: none` — some browsers defer image
// loading for hidden subtrees, which breaks `<picture>` source selection.
const OFFSCREEN_STYLE =
  "position:absolute;width:0;height:0;visibility:hidden;pointer-events:none;";

/**
 * Builds a picture element with source elements for responsive image selection.
 */
function buildPictureElement(resizedImages, fallbackUrl) {
  const picture = document.createElement("picture");
  picture.style.cssText = OFFSCREEN_STYLE;

  for (const size of SIZE_BREAKPOINTS) {
    for (const ratio of RATIO_THRESHOLDS) {
      for (const type of ["webp", "orig"]) {
        const key = `${size.key}_${ratio.key}_${type}`;
        const url = resizedImages[key];
        if (!url) continue;

        const source = document.createElement("source");
        source.srcset = url;

        const mediaConditions = [];
        if (ratio.minRatio) {
          mediaConditions.push(
            `(-webkit-min-device-pixel-ratio: ${ratio.minRatio})`
          );
        }
        if (size.maxWidth) {
          mediaConditions.push(`(max-width: ${size.maxWidth}px)`);
        }
        if (mediaConditions.length > 0) {
          source.media = mediaConditions.join(" and ");
        }

        if (type === "webp") {
          source.type = "image/webp";
        }

        picture.appendChild(source);
      }
    }
  }

  const img = document.createElement("img");
  img.src = fallbackUrl;
  picture.appendChild(img);

  return picture;
}

// Picks best URL via native `<picture>` source selection. URL is resolved
// once at connect time — viewport changes (resize, rotate) after that do not
// re-pick. Acceptable for the groundplan modal where users rarely resize
// mid-view.
async function getResponsiveImageUrl(resizedImages, fallbackUrl) {
  if (!resizedImages || Object.keys(resizedImages).length === 0) {
    return fallbackUrl;
  }

  const picture = buildPictureElement(resizedImages, fallbackUrl);
  const img = picture.querySelector("img");
  document.body.appendChild(picture);

  try {
    await img.decode();
    return img.currentSrc || img.src;
  } catch {
    return fallbackUrl;
  } finally {
    picture.remove();
  }
}

window.customElements.define(
  "leaflet-groundplan",
  class LeafletGroundplan extends HTMLElement {
    async connectedCallback() {
      const groundplan = L.map(this, {
        crs: L.CRS.Simple,
        minZoom: -5, //TODO: Calculate minZoom from GroundplanSize or something
        zoomControl: false,
        attributionControl: false,
        gestureHandling: true,
        gestureHandlingText: gestureHandlingText
      });
      this._groundplan = groundplan;

      L.control
        .zoom({
          position: "topright"
        })
        .addTo(groundplan);

      const bounds = [
        [0, 0],
        [1000, 1000]
      ];

      const imageUrl = await getResponsiveImageUrl(
        this.params.groundplanResizedImages,
        this.params.groundplanImageUrl
      );
      if (this._groundplan !== groundplan) return;

      L.imageOverlay(imageUrl, bounds).addTo(groundplan);
      groundplan.fitBounds(bounds);
      groundplan.setZoom(groundplan.getZoom() + 1, { animate: false });

      groundplan.setMaxBounds(bounds);
      groundplan.on("drag", function () {
        groundplan.panInsideBounds(bounds, { animate: false });
      });

      const groundplanMarkers = L.featureGroup();
      this.params.pois.forEach((poi, index) => {
        const groundplanIcon = L.divIcon({
          className: "groundplan-marker",
          html: index + 1,
          iconSize: [24, 24]
        });
        const poiMarker = L.marker([poi.yCoord, poi.xCoord], {
          icon: groundplanIcon
        }).addTo(groundplan);

        poiMarker.on("click", () => document._app.ports.groundplanPoiSelected.send(poi.id));

        groundplanMarkers.addLayer(poiMarker);
      });
      groundplan.addLayer(groundplanMarkers);
    }

    disconnectedCallback() {
      removeMap(this._groundplan);
      this._groundplan = null;
    }
  }
);
